import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, NotificationContextType, ConfirmOptions } from '../types/notification';
import { ToastContainer } from '../components/ui/Toast';
import { ConfirmModal } from '../components/modals';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  // Toast functions
  const showToast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      id,
      autoHide: true,
      duration: 4000,
      ...toastData,
    };

    setToasts(prev => [...prev, newToast]);
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Shorthand toast functions
  const showSuccess = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'success', title, message });
    },
    [showToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'error', title, message });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'warning', title, message });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'info', title, message });
    },
    [showToast]
  );

  // Confirm modal function
  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      setConfirmModal({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleConfirmModalClose = useCallback(
    (result: boolean) => {
      if (confirmModal) {
        confirmModal.resolve(result);
        setConfirmModal(null);
      }
    },
    [confirmModal]
  );

  const contextValue: NotificationContextType = {
    showToast,
    hideToast,
    showConfirm,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onHideToast={hideToast} />

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          {...confirmModal.options}
          onConfirm={() => handleConfirmModalClose(true)}
          onClose={() => handleConfirmModalClose(false)}
        />
      )}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notification context
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Helper hook for common notification patterns
export const useNotifications = () => {
  const { showSuccess, showError, showWarning, showInfo, showConfirm } = useNotification();

  const confirmDelete = useCallback(
    (itemName: string = 'kayıt') =>
      showConfirm({
        title: 'Silme Onayı',
        message: `Bu ${itemName} silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
        confirmText: 'Sil',
        cancelText: 'İptal',
        type: 'danger',
      }),
    [showConfirm]
  );

  const confirmAction = useCallback(
    (title: string, message: string, confirmText: string = 'Onayla') =>
      showConfirm({
        title,
        message,
        confirmText,
        cancelText: 'İptal',
        type: 'warning',
      }),
    [showConfirm]
  );

  const notifySuccess = useCallback(
    (operation: string) => showSuccess('İşlem Başarılı', `${operation} başarıyla tamamlandı.`),
    [showSuccess]
  );

  const notifyError = useCallback(
    (operation: string, error?: string) =>
      showError(
        'İşlem Başarısız',
        error || `${operation} sırasında bir hata oluştu. Lütfen tekrar deneyin.`
      ),
    [showError]
  );

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    confirmDelete,
    confirmAction,
    notifySuccess,
    notifyError,
  };
};
